import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Star, Minus, Plus, ShoppingCart, Video, ChevronLeft, Info, ChevronDown, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { CartNotification } from '../components/CartNotification';
import { CustomerReviews } from '../components/CustomerReviews';
import { ProductDisplaySection } from '../components/ProductDisplays';
import { ProductImageSlider } from '../components/ProductImageSlider';
import { fetchSingleProduct, fetchProductsLight } from '../services/products';
import type { Product, ProductSpecification } from '../types/product';
import { POPULAR_PRODUCTS } from '../data/popularProducts';

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState<Product | null>(null);
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);
  const [discountedProducts, setDiscountedProducts] = useState<Product[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { addItem } = useCart();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProduct, setNotificationProduct] = useState<{name: string; image: string} | null>(null);
  const [activeTab, setActiveTab] = useState(0); // Default tab is 0 (TOOTEKIRJELDUS)
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Gallery images for product page
  const galleryImages = [
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273983/UGC1.1_epveov.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273897/UGC2.1_da6skn.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273383/UGC3.1_eb0ltw.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273896/UGC4.1_fs8e4j.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273557/UGC5.2_f5s9ry.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273897/UGC6.1_d8hxfz.jpg'
  ];

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) {
        navigate('/toukeratas');
        return;
      }

      try {
        setIsLoading(true);
        console.log(`Loading product for variant: ${id}`);

        // Use the new single product fetch with caching
        const foundProduct = await fetchSingleProduct(id);

        if (foundProduct) {
          setProduct(foundProduct);
          const variant = foundProduct.variants.find(v => v.id === id);
          setSelectedVariantId(variant?.id || foundProduct.variants[0].id);
          setCurrentImageIndex(0);
        } else {
          console.error(`Product not found for variant: ${id}`);
          navigate('/toukeratas');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/toukeratas');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();

    // Fetch popular products using lightweight data
    const fetchPopularProducts = async () => {
      try {
        const data = await fetchProductsLight();
        const populars = POPULAR_PRODUCTS.map(popularProduct => {
          const fullProduct = data.find((p: Product) => p.id === popularProduct.id);
          if (fullProduct && popularProduct.variants && popularProduct.variants[0].id) {
            // Filter the variants to only include the specified variant ID
            fullProduct.variants = fullProduct.variants.filter(v => 
              popularProduct.variants.some(pv => pv.id === v.id)
            );
          }
          return fullProduct || null;
        }).filter(Boolean);
        setPopularProducts(populars);

        // Get discounted products
        const discounted = data
          .filter((p: Product) => p.variants?.some(v => v.discounted_price))
          .slice(0, 5);
        setDiscountedProducts(discounted);
      } catch (error) {
        console.error('Error fetching popular products:', error);
      }
    };

    fetchPopularProducts();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-20 px-6">
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4FC1E9] mx-auto mb-4"></div>
            <p className="text-gray-600">Laadin toodet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product || !selectedVariantId) {
    return <div className="container mx-auto py-20 px-6">Toodet ei leitud</div>;
  }

  const selectedVariant = product.variants.find(v => v.id === selectedVariantId);
  if (!selectedVariant) {
    return <div className="container mx-auto py-20 px-6">Toote varianti ei leitud</div>;
  }

  const handleAddToCart = () => {
    // Ensure quantity doesn't exceed available stock
    const safeQuantity = Math.min(quantity, selectedVariant.stock || 0);
    
    addItem({
      id: selectedVariant.id,
      name: selectedVariant.name || (product.name ? `${product.name} - ${selectedVariant.color}` : `Toode - ${selectedVariant.color}`),
      price: selectedVariant.discounted_price || selectedVariant.price,
      quantity: safeQuantity,
      image: selectedVariant.images[0],
      stock: selectedVariant.stock // Pass stock to enforce limits
    });
    
    setNotificationProduct({
      name: selectedVariant.name || (product.name ? `${product.name} - ${selectedVariant.color}` : `Toode - ${selectedVariant.color}`),
      image: selectedVariant.images[0]
    });
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  const getStockStatus = (stock: number) => {
    if (stock > 3) {
      return <span className="text-green-500">Tellitav</span>;
    } else if (stock === 2 || stock === 3) {
      return <span className="text-orange-400">Vaid {stock} tellitavad!</span>;
    } else if (stock === 1) {
      return <span className="text-orange-500">Viimane tellitav!</span>;
    } else {
      return <span className="text-red-500">Laost otsas</span>;
    }
  };

  // Get the icon color class based on stock level
  const getStockIconColor = (stock: number) => {
    if (stock > 3) {
      return 'text-green-500';
    } else if (stock === 2 || stock === 3) {
      return 'text-orange-400';
    } else if (stock === 1) {
      return 'text-orange-500';
    } else {
      return 'text-red-500';
    }
  };

  // Map product class to Estonian label for use in meta descriptions / fallbacks
  const getProductClassLabel = (cls: string): string => {
    const labels: Record<string, string> = {
      'complete-scooters': 'trikitõukeratas',
      'decks': 'tõukeratta tald',
      'bars': 'tõukeratta lenks',
      'wheels': 'tõukeratta ratas',
      'forks': 'tõukeratta kahvel',
      'clamps': 'tõukeratta klamber',
      'bar-ends': 'tõukeratta lenksuotsad',
      'pegs': 'tõukeratta peg',
      'grips': 'tõukeratta käepide',
      'griptape': 'tõukeratta gripteip',
    };
    return labels[cls] || 'trikitõukeratta varuosa';
  };

  // Map product class to its subcategory route for breadcrumbs
  const getClassRoute = (cls: string): { name: string; path: string } | null => {
    const routes: Record<string, { name: string; path: string }> = {
      'complete-scooters': { name: 'Tõukerattad', path: '/toukeratas' },
      'decks': { name: 'Tallad', path: '/toukeratas/tald' },
      'bars': { name: 'Lenksud', path: '/toukeratas/lenks' },
      'wheels': { name: 'Rattad', path: '/toukeratas/rattad' },
      'forks': { name: 'Kahvlid', path: '/toukeratas/kahvlid' },
      'clamps': { name: 'Klambrid', path: '/toukeratas/klambrid' },
      'bar-ends': { name: 'Lenksuotsad', path: '/toukeratas/lenksuotsad' },
      'pegs': { name: 'Peegid', path: '/toukeratas/peegid' },
      'grips': { name: 'Käepidemed', path: '/toukeratas/kaepidemed' },
      'griptape': { name: 'Gripteibid', path: '/toukeratas/gripteibid' },
    };
    return routes[cls] ?? null;
  };

  // Generate category-aware FAQ items based on product class/category
  type FaqItem = { q: string; a: string };

  const getProductFaqs = (productClass: string, categories: string[], brand: string): FaqItem[] => {
    const shippingReturns: FaqItem[] = [
      {
        q: 'Kui kiiresti tellimus kohale jõuab?',
        a: 'Laos olevad tooted saadame 1–2 tööpäeva jooksul. Kohaletoimetamine DPD, Omniva või SmartPosti kaudu võtab Eestis aega 1–3 tööpäeva. Tellitavad tooted jõuavad kätte 5–14 tööpäeva jooksul. Saadetise staatust saad jälgida tracking-koodi abil.',
      },
      {
        q: 'Kas saan toote tagastada?',
        a: 'Jah, sul on 14 kalendripäeva aega kaup tagastada alates kättesaamise päevast. Tagastatav toode peab olema kasutamata, originaalpakendis ja terves seisukorras. Defektse toote puhul katame tagastuskulud meie.',
      },
      {
        q: 'Kas toode on originaal ja millega see garanteeritud?',
        a: `Jah, kõik Tõuks.ee tooted on 100% originaalid ametlikelt edasimüüjatelt. ${brand} toodetele kehtib tootja garantii tootmisdefektide vastu. Garantii ei kata normaalset kulumist ega kasutaja põhjustatud kahjustusi.`,
      },
    ];

    if (productClass === 'decks') {
      return [
        {
          q: 'Kuidas teada, kas see tald ühildub minu tõuksiga?',
          a: 'Enne ostu kontrolli kolme asja: (1) rattasuurus — tald peab mahutama sinu rataste läbimõõtu (100, 110 või 120 mm); (2) peatoru tüüp — integreeritud headtube vajab integreeritud headset-i; (3) kompressioonisüsteem (SCS/HIC/IHC). Kahtluse korral kirjuta meile!',
        },
        {
          q: 'Kas see tald sobib park- või street-sõitluseks?',
          a: categories.includes('Street')
            ? 'See tald on mõeldud street-sõitluseks — laiem kuju ja box-cut disain annavad parema grindimispinna relsidel ja ledge\'idel. Tugevam konstruktsioon talub tänavaülesandeid.'
            : categories.includes('Park')
            ? 'See tald on mõeldud park-sõitluseks — kitsam ja kergem, et võimaldada kiiremaid rotatsioone rampidel. Ideal tailwhip\'ide ja barspin\'ide jaoks.'
            : 'See tald sobib nii park- kui street-sõitluseks. Talla laius määrab sobivaima kasutuse — kitsam tald (alla 5") on parem pargis, laiem (5.5"+) tänaval.',
        },
        {
          q: 'Kas tald on kokku pandud või tulevad osad eraldi?',
          a: 'Tald tarnitakse ilma teiste komponentideta — ilma rattaste, peatoru ja kahvlita. See on eraldi osa, mis sobib olemasolevatesse tõukse või custom-ehitusse. Griptape ei pruugi olla kaasas — kontrolli tootekirjeldusest.',
        },
        {
          q: 'Milline materjal on parim talla vastupidavuseks?',
          a: 'Alumiinium 6061-T6 või 6082-T6 on parim valik jõudluse ja kaalu vahel. See on kerge, tugev ja korrosioonikindel. Pro-taseme tallad on sageli kõrgemal klass alumiiniumist, mis on kergem ja tugevam kui standard alumiinium.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'bars') {
      return [
        {
          q: 'Millise kompressioonisüsteemiga see lenks ühildub?',
          a: 'Lenksu ühilduvus sõltub materjalist ja siseläbimõõdust: alumiiniumlenksud (ühilduvad IHC ja SCS); standardsed teraslenksud slitiga (ühilduvad IHC ja HIC); oversized teraslenksud slitiga (ühilduvad HIC ja SCS). Vaata tootelehtül kompressiooni andmeid või küsige meie käest.',
        },
        {
          q: 'Kuidas valida õiget lenksu kõrgust?',
          a: 'Lenks peaks ulatuma puusast nabani, kui seisad tõukse peal. Park-sõitjad eelistavad madalamaid lenksusid (õhukontrolli jaoks), street-sõitjad kõrgemaid (grindide stabiilsuse jaoks). Algajad peaksid valima keskmise kõrguse.',
        },
        {
          q: 'Kas lenksu saab lühemaks lõigata?',
          a: 'Jah, alumiinium- ja teraslenksuid saab metallisakiga lõigata soovitud kõrgusele. Oluline: lõika alati väiksema järgse mõõdu järgi, sest liialt lõigatud lenksu ei saa pikemaks teha. Pärast lõikamist puhasta ja viiluta ääred.',
        },
        {
          q: 'Mis materjalist on parem — alumiinium või teras?',
          a: 'Alumiinium on kergem — sobib park-sõitjatele, kes vajavad kerget seadistust õhutrikkideks. Teras on raskem ja tugevam — sobib street-sõitjatele, kes peavad vastu tänava koormustele. Titaan on kõige kergem ja tugevaim, kuid ka kalleim valik.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'complete-scooters') {
      const isBeginnerCategory = categories.includes('Kesktase') || (!categories.includes('Pro tase') && !categories.includes('Street') && !categories.includes('Park'));
      return [
        {
          q: 'Kas see tõukeratas on kohe sõiduks valmis?',
          a: 'Complete tõuksid tarnitakse osaliselt lahtiselt pakendis. Tavaliselt tuleb kinnitada lenks klambriga ja mõnikord paigaldada esiratas — kogu protsess võtab 5–10 minutit. Kaasas on paigaldusjuhend.',
        },
        {
          q: `Mis tasemel sõitjale see ${brand} sobib?`,
          a: isBeginnerCategory
            ? `See ${brand} mudel sobib alustajatele ja kesktaseme sõitjatele — töökindel, ohutu ja hea hinna-kvaliteedi suhtega. Sobiv vanus on enamasti 8–16 aastat, kuid sobib ka täiskasvanud algajatele.`
            : `See ${brand} mudel on mõeldud kogenud sõitjatele, kes sooritavad keerulisi trikke skatepargis. Premium komponendid ja täiustatud jõudlus pro-taseme sõidu jaoks.`,
        },
        {
          q: 'Kas saab osi hiljem vahetada?',
          a: 'Jah! Complete tõuksi kõiki osi — talda, lenksu, rattaid, kahvlit, klambrit — saab hiljem eraldi uuendada. See võimaldab tõuks kohandada oma areneva sõitlustiili järgi. Kontrolli enne ostmist, et uued osad ühilduksid olemasoleva kompressioonisüsteemiga.',
        },
        {
          q: 'Milliseid kaitsevahendeid peaks kandma?',
          a: 'Vähemalt kiiver on kohustuslik — see on kõige olulisem kaitsevahend. Lisaks soovitame põlvekaitseid ja külnarkaitseid eriti õppimise faasis. Randmekaitsed on eriti olulised esimeste trikkide õppimisel.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'wheels') {
      return [
        {
          q: 'Milline rattasuurus on minu tõuksile õige?',
          a: '100 mm rattad on kergeid ja sobivad park-sõitjatele, kes vajavad kiiret rotatsiooni. 110 mm on universaalne valik — hea kiirendus ja stabiilsus. 120 mm rattad on suuremad, annavad kõrgema kiiruse ja sujuvama sõidu tänaval. Kontrolli talla spetsifikatsiooni, et teada maksimaalne rattasuurus, mis mahub sinu tallale.',
        },
        {
          q: 'Mis vahe on alumiinium- ja nailonist rattasüdamikul?',
          a: 'Alumiiniumsüdamik on tugevam, kergem ja kestab oluliselt kauem — see on valik kogenud sõitjatele. Nailonsüdamik on odavam, kuid murrab kergemini tugeva kukkumise või grindimise käigus. Pro-taseme sõitjad kasutavad alati alumiiniumsüdamikuga rattaid.',
        },
        {
          q: 'Kas rattad tulevad laagritega?',
          a: 'Enamik tõukeratta rattaid tarnitakse laagritega koos (ABEC-7 või ABEC-9 laagrid). Vaata toote kirjeldusest, kas laagrid on komplektis. Kui laagrid ei ole kaasas, sobivad standardsed 608 ZZ laagrid (8 mm sisediameeter).',
        },
        {
          q: 'Millal tuleb rattad välja vahetada?',
          a: 'Vaheta rattad siis, kui uretan (kummikiht) on nii kulunud, et südamik hakkab pinda puutuma, ratas on pragunenud, või sõit muutub ebatasaseks ja mürarikkaks. Profisõitjad vahetavad rattaid iga 3–6 kuu järel intensiivse kasutuse korral.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'forks') {
      return [
        {
          q: 'Mis kompressioonisüsteemiga see kahvel ühildub?',
          a: 'Kahvli kompressiooniühilduvus sõltub kahvli peatoru tüübist: IHC kahvlid töötavad koos IHC lenksuga (slitiga, standardne läbimõõt); HIC kahvlid nõuavad HIC/SCS lenksu (oversized, slitiga); SCS kahvlid töötavad peaaegu iga lenksuga. Vaata tootelehtül täpseid ühilduvuse andmeid.',
        },
        {
          q: 'Kas kahvel on ühilduv minu talla rattaauguga?',
          a: 'Kontrolli kahvel rattaauku (dropout) ja su ratta teljeläbimõõtu (tavaliselt 8 mm). Enamik standardseid kahvleid on 8 mm telgega ühilduvad. Lisaks veendu, et kahvel on õige rattasuuruse jaoks — 100 mm, 110 mm või 120 mm rattad nõuavad vastavaid kahvleid.',
        },
        {
          q: 'Milline materjal on parem — alumiinium või teras?',
          a: 'Alumiiniumkahvel on kergem — sobib park-sõitjatele, kes vajavad kerget seadistust õhutrikkideks. Teraskahvel on raskem, kuid tugevam — sobib street-sõitjatele, kes peavad vastu tänavasõidu koormustele. Enamik pro-sõitjaid kasutab alumiiniumist kahvlit.',
        },
        {
          q: 'Kas pean ostma ka headset-i (peatoru laagrikomplekt)?',
          a: 'See sõltub sinu talla peatoru tüübist ja kahvli tüübist. Kui vaheta ainult kahvlit (sama tüüpi), võib vana headset sobida edasi. Kui vaheta talla või kahvli tüüpi (nt IHC → SCS), on uus headset vajalik. Kahtluse korral kirjuta meile!',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'clamps') {
      return [
        {
          q: 'Mis vahe on single-bolt ja double-bolt klambril?',
          a: 'Single-bolt klamber (1 polt) on kergem ja lihtsam — sobib park-sõitjatele ja kergemale kasutusele. Double-bolt klamber (2 polti) on tugevam kinnitus — sobib street-sõitjatele ja neile, kelle lenks kipub libisema. Täiendavad bolti arved (4-bolt) annavad maksimaalse kinnitusjõu pro-taseme kasutuseks.',
        },
        {
          q: 'Kas see klamber sobib minu kompressioonisüsteemiga?',
          a: 'Klamber töötab koos SCS kompressioonisüsteemiga, kus klamber surub lenksu otse peatoru peale. IHC ja HIC süsteemide puhul täidab klamber ainult lenksu kinnitamise rolli, mitte kompressiooni. Vaata tootelehtül, milliste kompressioonisüsteemidega klamber sobib.',
        },
        {
          q: 'Kuidas teada, milline klamber ühildub minu lenksuga?',
          a: 'Klamber peab ühilduma lenksu peatoru välisläbimõõduga: standardne lenks → standard klamber (31.8 mm sisediameeter); oversized lenks → oversized klamber (34.9 mm sisediameeter). Kontrolli alati lenksu spetsifikatsioonist selle välisläbimõõtu.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'bar-ends') {
      return [
        {
          q: 'Mis on lenksuotsad (bar ends) ja milleks neid kasutatakse?',
          a: 'Lenksuotsad on lenksu otste sisse paigaldatavad käepidemed. Need laiendavad lenksu käepidemisvõimalusi, kaitsevad lenksu otsi kukkumiste ja grindimise eest ning parandavad haaret teatud trikkide ajal. Paljud sõitjad kasutavad neid ka esteetilisel eesmärgil.',
        },
        {
          q: 'Kas lenksuotsad on universaalsed ja sobivad kõigile lenksudele?',
          a: 'Enamik lenksuotsi on mõeldud standardse siseläbimõõduga (22 mm) lenksudele. Enne ostu kontrolli oma lenksu siseläbimõõtu. Alumiiniumist lenksuotsad on kergemad, teraskst vastupidavamad. Paigaldamine on lihtne — lüka sisse ja kinnita vajadusel poldiga.',
        },
        {
          q: 'Kas lenksuotsad mõjutavad grindimist?',
          a: 'Jah — raudsed/alumiiniumist lenksuotsad on eelistatud bar-grinding trikkideks (grinding lenksuga relssil), kuna need libisevad paremini. Pehmemad materjalid võivad takerduda. Vaata, kas toote kirjelduses on mainitud grind-sobivust.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'pegs') {
      return [
        {
          q: 'Mis on tõukeratta peg-id ja milleks neid kasutatakse?',
          a: 'Peg-id on metallsilindrid, mis paigaldatakse rattatelgede otstesse. Neid kasutatakse grindimistrikside jaoks (sliding pegi mööda relssi, äärte või ledge\'e). Peg-id on olulised vahend street-sõitjatele, kes soovivad teha grinding-trikke.',
        },
        {
          q: 'Millise suurusega peg-id mulle sobivad?',
          a: 'Peg-id peavad sobima su tõukeratta rattatelge läbimõõduga (tavaliselt 8 mm). Pegi pikkus mõjutab grinding-tasakaalu — pikemad peg-id (4"+) annavad rohkem grippe, lühemad (3–3.5") on kergema kaaluga. Alumiiniumist peg-id on kergemad, terased vastupidavamad grindimiseks.',
        },
        {
          q: 'Kas peg-id paigaldatakse ees- või tagarattale?',
          a: 'Peg-id saab paigaldada nii ees- kui tagarattale. Enamik sõitjaid alustab kahe taga-pegi\'ga. Ees-peg-id on lisavõimalus keerulisemate trikkide jaoks. Tavaliselt kasutatakse 2–4 peg-i korraga, sõltuvalt sõitlusstiilist.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'grips') {
      return [
        {
          q: 'Kuidas valida sobivad käepidemed (grips)?',
          a: 'Käepidemete valikul arvesta: (1) materjal — vahtpolüuretan (foam) on pehme ja kerge, gumi on vastupidavam; (2) laius — paksemad grips-id on mugavamad, õhemad annavad parema tunde lenksul; (3) pikkus — kontrolli, et grips mahuksid su lenksu otsa. Enamik grippe on standardpikkuses (150–170 mm).',
        },
        {
          q: 'Kuidas käepidemed kinnitada ja eemaldada?',
          a: 'Paigaldamiseks: piserda lenksu otsa piritust või kasuta õhupumpa lenksu alla. Grips peaks libisema sisse ilma liimita — ärge kasutage liimi, sest siis on eemaldamine keeruline. Eemaldamiseks: kasuta teravat lõikurit lenksu ja gripi vahele, seejärel pöörle grips maha. Alternatiiv: puhub grips mahavõtmiseks õhu lenksu otsa sisse.',
        },
        {
          q: 'Kui tihti tuleks käepidemed vahetada?',
          a: 'Vaheta käepidemed siis, kui need on liiga libedad, rebenenud või kulunud. Intensiivse kasutuse korral vahetab enamik sõitjaid grips-id 3–6 kuu järel. Märk vahetuse vajadusest on see, kui käed libisevad trikke tehes lenksult.',
        },
        ...shippingReturns,
      ];
    }

    if (productClass === 'griptape') {
      return [
        {
          q: 'Kuidas gripteip tõukeratta tallale paigaldada?',
          a: 'Lõika gripteip vastavalt talla suurusele. Eemalda vana gripteip (kasuta soojuspüstolit soojenemiseks). Puhasta talla pind. Koori uuelt gripteibilt kile tagant ära ja surude talla pinnale alates keskelt väljapoole, et mulle ei jääks. Viiluta äärtest üle ja lõika üleliigne osa ära.',
        },
        {
          q: 'Kui tihti tuleks gripteip vahetada?',
          a: 'Vaheta gripteip, kui see hakkab tõstma, kulub liiga siledale, kleepub ebaühtlaselt, või on nähtavalt rebenenud. Intensiivse kasutuse korral vahetab enamik sõitjaid gripteip iga 2–4 kuu tagant. Märk vajadusest on jalg hakkab trikke tehes tallal libisema.',
        },
        {
          q: 'Kas gripteip on universaalne kõikidele talladele?',
          a: 'Jah, tõukeratta gripteip on standardne laiusega (tavaliselt 10–11 cm) ja lõigatakse vastavalt talla suurusele. Paksem gripteip annab rohkem haaret, õhem on kergem. Enamik gripteibisid on self-adhesive (isekleepuv) — pole vaja lisaliimi.',
        },
        ...shippingReturns,
      ];
    }

    // Generic fallback for other product types
    return [
      {
        q: 'Kas see toode sobib minu tõuksiga?',
        a: 'Kontrolli tootelehtül täpseid mõõte ja ühilduvust. Kahtluse korral kirjuta meile — aitame hea meelega ühilduvust kontrollida enne ostu.',
      },
      ...shippingReturns,
    ];
  };

  // Find specifications in the new format
  const productDescription = product.specifications?.find(spec => spec.title === "TOOTEKIRJELDUS");
  const technicalSpecs = product.specifications?.find(spec => spec.title === "TEHNILISED ANDMED");

  const handleIncreaseQuantity = () => {
    // Don't allow increasing beyond stock
    if (quantity < (selectedVariant.stock || 0)) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    // Minimum quantity is 1
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  // Handle variant change with image index preservation
  const handleVariantChange = (variantId: string) => {
    const newVariant = product.variants.find(v => v.id === variantId);
    if (newVariant) {
      // If the new variant has fewer images than the current image index,
      // reset to the first image, otherwise keep the current position
      if (newVariant.images && currentImageIndex >= newVariant.images.length) {
        setCurrentImageIndex(0);
      }
      
      setSelectedVariantId(variantId);
      // Reset quantity to 1 when changing variants
      setQuantity(1);
    }
  };

  // Check for new badge - support both new and isNew properties
  const isNew = product.isNew || product.new || selectedVariant.isNew || selectedVariant.new;

  // Generate structured data for SEO
  const generateStructuredData = () => {
    const baseUrl = 'https://xn--tuks-0qa.ee';
    const productUrl = `${baseUrl}/tooted/${selectedVariant.id}`;
    
    // Get product description from specifications
    const description = productDescription?.description || 
                       selectedVariant.description?.join(' ') || 
                       `${selectedVariant.name} - ${product.brand} trikitõukeratas`;
    
    // Get product images with full URLs
    const productImages = selectedVariant.images.map(img => 
      img.startsWith('http') ? img : `${baseUrl}${img}`
    );

    // Determine availability
    const availability = selectedVariant.stock > 0 ? 
      'https://schema.org/InStock' : 
      'https://schema.org/OutOfStock';

    // Get current price (discounted or regular)
    const currentPrice = selectedVariant.discounted_price || selectedVariant.price;

    // Build category hierarchy
    const categoryPath = product.category?.join(' > ') || product.class;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": selectedVariant.name,
      "description": description,
      "image": productImages,
      "url": productUrl,
      "sku": selectedVariant.id,
      "brand": {
        "@type": "Brand",
        "name": product.brand
      },
      "category": categoryPath,
      "offers": {
        "@type": "Offer",
        "price": currentPrice.toString(),
        "priceCurrency": "EUR",
        "availability": availability,
        "itemCondition": "https://schema.org/NewCondition",
        "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        "url": productUrl,
        "seller": {
          "@type": "Organization",
          "name": "Tõuks.ee",
          "url": baseUrl
        },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": "3.99",
            "currency": "EUR"
          },
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "EE"
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 0,
              "maxValue": 1,
              "unitCode": "DAY"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 1,
              "maxValue": 5,
              "unitCode": "DAY"
            }
          }
        },
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "applicableCountry": "EE",
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 14,
          "returnMethod": "https://schema.org/ReturnByMail",
          "returnFees": "https://schema.org/FreeReturn",
          "refundType": "https://schema.org/FullRefund"
        }
      },
      ...(selectedVariant.review_count && selectedVariant.review_count > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": selectedVariant.review_count,
          "bestRating": "5",
          "worstRating": "1"
        }
      } : {})
    };

    // Add high price if there's a discount
    if (selectedVariant.discounted_price) {
      structuredData.offers.highPrice = selectedVariant.price.toString();
    }

    // Add additional properties
    if (product.tags && product.tags.length > 0) {
      structuredData.keywords = product.tags.join(', ');
    }

    // Add color variant
    structuredData.color = selectedVariant.color;

    // Add manufacturer details
    structuredData.manufacturer = {
      "@type": "Organization",
      "name": product.brand
    };

    return structuredData;
  };

  return (
    <div className="container mx-auto py-12 md:py-20 px-4 md:px-6">
      <Helmet>
        <title>{selectedVariant.name} | Tõuks.ee</title>
        <meta name="description" content={`${selectedVariant.name} - ${product.brand} ${getProductClassLabel(product.class)}. ${productDescription?.description || `Kvaliteetne ${getProductClassLabel(product.class)} professionaalidele ja harrastajatele.`} Hind: ${(selectedVariant.discounted_price || selectedVariant.price).toFixed(2)}€. ${selectedVariant.stock > 0 ? 'Tellitav' : 'Laost otsas'}.`} />
        <meta name="keywords" content={`${selectedVariant.name}, ${product.brand}, ${getProductClassLabel(product.class)}, trikitõuks, tõuks, scooter, trick scooter, ${product.tags?.join(', ') || ''}, pro trikitõukeratas, sobib lastele, freestyle`} />
        <meta property="og:title" content={`${selectedVariant.name} | Tõuks.ee`} />
        <meta property="og:description" content={`${selectedVariant.name} - ${product.brand} trikitõukeratas. Hind: ${(selectedVariant.discounted_price || selectedVariant.price).toFixed(2)}€. ${selectedVariant.stock > 0 ? 'Tellitav' : 'Laost otsas'}.`} />
        <meta property="og:image" content={selectedVariant.images[0]?.startsWith('http') ? selectedVariant.images[0] : `https://xn--tuks-0qa.ee${selectedVariant.images[0]}`} />
        <meta property="og:url" content={`https://xn--tuks-0qa.ee/tooted/${selectedVariant.id}`} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={(selectedVariant.discounted_price || selectedVariant.price).toString()} />
        <meta property="product:price:currency" content="EUR" />
        <meta property="product:availability" content={selectedVariant.stock > 0 ? 'in stock' : 'out of stock'} />
        <meta property="product:brand" content={product.brand} />
        <meta property="product:condition" content="new" />
        <link rel="canonical" href={`https://xn--tuks-0qa.ee/tooted/${selectedVariant.id}`} />
        
        {/* Product structured data */}
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData(), null, 2)}
        </script>

        {/* BreadcrumbList — includes subcategory level when applicable */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": (() => {
            const base = [
              { "@type": "ListItem", "position": 1, "name": "Kodu", "item": "https://xn--tuks-0qa.ee/" },
              { "@type": "ListItem", "position": 2, "name": "Tõukerattad", "item": "https://xn--tuks-0qa.ee/toukeratas" },
            ];
            const subcat = getClassRoute(product.class);
            if (subcat && subcat.path !== '/toukeratas') {
              return [
                ...base,
                { "@type": "ListItem", "position": 3, "name": subcat.name, "item": `https://xn--tuks-0qa.ee${subcat.path}` },
                { "@type": "ListItem", "position": 4, "name": selectedVariant.name, "item": `https://xn--tuks-0qa.ee/tooted/${selectedVariant.id}` },
              ];
            }
            return [
              ...base,
              { "@type": "ListItem", "position": 3, "name": selectedVariant.name, "item": `https://xn--tuks-0qa.ee/tooted/${selectedVariant.id}` },
            ];
          })()
        })}</script>

        {/* VideoObject — if the product has a video, surfaces it in video search */}
        {product.video && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": `${selectedVariant.name} - tootevideo`,
            "description": `${selectedVariant.name} tootevideo. ${product.brand} trikitõukeratas.`,
            "thumbnailUrl": selectedVariant.images[0]?.startsWith('http')
              ? selectedVariant.images[0]
              : `https://xn--tuks-0qa.ee${selectedVariant.images[0]}`,
            "contentUrl": product.video,
            "embedUrl": product.video,
            "uploadDate": "2024-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Tõuks.ee",
              "url": "https://xn--tuks-0qa.ee"
            }
          })}</script>
        )}

        {/* FAQPage Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": getProductFaqs(product.class, product.category || [], product.brand).map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a
            }
          }))
        })}</script>
      </Helmet>

      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-gray-600 hover:text-[#4FC1E9] mb-6 md:mb-8"
      >
        <ChevronLeft className="w-5 h-5 mr-1" />
        Tagasi toodetele
      </button>

      {showNotification && notificationProduct && (
        <CartNotification
          product={{
            name: notificationProduct.name,
            image: notificationProduct.image
          }}
          isVisible={showNotification}
          onClose={() => setShowNotification(false)}
        />
      )}

      {/* Made the product card wider and more mobile-friendly */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Product Images Section */}
          <div className="p-4 md:p-8">
            <div className="aspect-square rounded-lg overflow-hidden mb-4 relative">
              {showVideo && product.video ? (
                <iframe
                  src={product.video}
                  className="w-full h-full"
                  allowFullScreen
                ></iframe>
              ) : (
                <ProductImageSlider 
                  images={selectedVariant.images}
                  name={selectedVariant.name || product.name || 'Toode'}
                  brand={product.brand}
                  productClass={product.class}
                  tags={product.tags}
                  className="rounded-lg"
                  padding="p-0"
                  badge={null} // Remove discount badge
                  newBadge={false} // Remove new badge
                  currentImageIndex={currentImageIndex}
                  onImageIndexChange={setCurrentImageIndex}
                />
              )}
              {product.video && (
                <button
                  onClick={() => setShowVideo(!showVideo)}
                  className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors duration-300 z-20"
                >
                  <Video className="w-6 h-6 text-black" />
                </button>
              )}
            </div>
            
            {/* Thumbnail grid - mobile responsive */}
            <div className="grid grid-cols-4 gap-2 md:gap-4">
              {selectedVariant.images.map((image, index) => (
                <button 
                  key={index} 
                  className={`aspect-square rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
                    currentImageIndex === index && !showVideo ? 'ring-2 ring-[#4FC1E9] scale-105' : 'hover:opacity-80'
                  }`}
                  onClick={() => {
                    setCurrentImageIndex(index);
                    setShowVideo(false);
                  }}
                >
                  <img 
                    src={image} 
                    alt={`${selectedVariant.name} - ${product.brand} trikitõukeratas - pilt ${index + 1}`} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (!target.src.includes('https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg')) {
                        target.src = 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';
                      }
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details Section */}
          <div className="p-4 md:p-8">
            {/* Badges and Rating */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {selectedVariant.discounted_price && (
                <span className="bg-[#4FC1E9] text-white text-sm px-3 py-1 rounded-full">
                  -{Math.round(((selectedVariant.price - selectedVariant.discounted_price) / selectedVariant.price) * 100)}%
                </span>
              )}
              {isNew && (
                <span className="bg-green-500 text-white text-sm px-3 py-1 rounded-full">UUS</span>
              )}
              <div className="flex items-center">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-gray-500 text-sm ml-2">({selectedVariant.review_count} arvustust)</span>
              </div>
            </div>

            {/* Product Name */}
            <h1 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
              {selectedVariant.name || `${product.name || 'Toode'} - ${selectedVariant.color}`}
            </h1>

            {/* ESTO Financing Banner */}
            <div className="mb-6 flex items-center gap-4">
              <img 
                src="https://res.cloudinary.com/daka2lob2/image/upload/v1751272738/ESTO_jarelmaks_be56fw.png" 
                alt="ESTO järelmaks" 
                className="h-8 w-auto"
                style={{ transform: 'scale(1.25)' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <div className="text-base">
                <div className="font-semibold text-gray-800">Kuni 60 kuud</div>
                <div className="text-gray-600">0€ lepingutasu!</div>
              </div>
            </div>
            
            {/* Price Section */}
            <div className="mb-6">
              {selectedVariant.discounted_price ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-3xl md:text-4xl font-bold text-[#4FC1E9]">
                    {selectedVariant.discounted_price.toFixed(2)}€
                  </span>
                  <span className="text-gray-400 line-through text-xl">
                    {selectedVariant.price.toFixed(2)}€
                  </span>
                </div>
              ) : (
                <span className="text-3xl md:text-4xl font-bold text-[#4FC1E9]">
                  {selectedVariant.price.toFixed(2)}€
                </span>
              )}
            </div>

            {/* Free courier delivery notice */}
            <div className="mb-6 flex items-center gap-3 bg-[#4FC1E9]/10 border border-[#4FC1E9]/30 rounded-lg px-4 py-3">
              <Truck className="w-5 h-5 text-[#4FC1E9] flex-shrink-0" />
              <span className="text-sm md:text-base font-semibold text-gray-800">
                Tasuta kulleriga koju kohaletoimetamine üle kogu Eesti!
              </span>
            </div>

            {/* Color Variants */}
            <div className="mb-6">
              <h2 className="font-bold mb-3">Värvid</h2>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => handleVariantChange(variant.id)}
                    className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                      selectedVariantId === variant.id 
                        ? 'border-[#4FC1E9] scale-110 shadow-lg' 
                        : 'border-transparent hover:border-gray-200 hover:scale-105'
                    }`}
                    style={{ backgroundColor: variant.color_code }}
                    title={variant.color}
                  />
                ))}
              </div>
            </div>

            {/* Product Description */}
            <div className="mb-6 space-y-3">
              {selectedVariant.description?.map((paragraph, index) => (
                <p key={index} className="text-gray-600 leading-relaxed">{paragraph}</p>
              ))}
            </div>

            {/* Quantity and Add to Cart */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
              {/* Quantity Selector */}
              <div className="flex items-center border rounded-lg self-start sm:self-auto">
                <button 
                  onClick={handleDecreaseQuantity}
                  disabled={quantity <= 1}
                  className="p-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className={`w-4 h-4 ${quantity <= 1 ? 'text-gray-300' : 'text-gray-600'}`} />
                </button>
                <span className="px-4 py-3 min-w-[3rem] text-center font-medium">{quantity}</span>
                <button 
                  onClick={handleIncreaseQuantity}
                  disabled={selectedVariant.stock <= 0 || quantity >= selectedVariant.stock}
                  className="p-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className={`w-4 h-4 ${selectedVariant.stock <= 0 || quantity >= selectedVariant.stock ? 'text-gray-300' : 'text-gray-600'}`} />
                </button>
              </div>

              {/* Add to Cart Button */}
              <button 
                onClick={handleAddToCart}
                disabled={selectedVariant.stock <= 0}
                className={`flex-1 sm:flex-initial ${
                  selectedVariant.stock > 0 
                    ? 'bg-[#4FC1E9] hover:bg-[#3ba8d0] shadow-lg hover:shadow-xl' 
                    : 'bg-gray-300 cursor-not-allowed'
                } text-white px-6 py-3 rounded-lg flex items-center justify-center transition-all duration-200 transform hover:scale-105 font-medium`}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {selectedVariant.stock > 0 ? 'Lisa korvi' : 'Laost otsas'}
              </button>
            </div>

            {/* Stock Status and Additional Info */}
            <h2 className="text-xl font-bold mb-4">Saadavus</h2>
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="font-medium">{getStockStatus(selectedVariant.stock)}</span>
                <div className="relative group">
                  <div 
                    className={`${getStockIconColor(selectedVariant.stock)} transition-colors cursor-help`}
                    title="Kohaletoimetamise info"
                  >
                    <Info className="w-4 h-4" />
                  </div>
                  <div className="absolute left-0 top-6 bg-gray-800 text-white text-xs rounded-lg p-3 w-64 z-10 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="mb-2 font-medium">Kohaletoimetamise aeg:</div>
                    <div className="mb-1">• Tellitavad tooted: 3-7 tööpäeva</div>
                    <div className="mb-1">• Laost otsas: ei saa tellida</div>
                    <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-800 transform rotate-45"></div>
                  </div>
                </div>
              </div>
              
              {/* Categories */}
              {product.category && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-gray-600">Kategooriad:</span>
                  {product.category.map(cat => (
                    <Link
                      key={cat}
                      to={`/toukeratas?category=${encodeURIComponent(cat)}`}
                      className="text-[#4FC1E9] hover:text-[#3ba8d0] hover:underline px-2 py-1 bg-[#4FC1E9]/10 rounded-full text-sm"
                    >{cat}</Link>
                  ))}
                </div>
              )}
              
              {/* Tags */}
              {product.tags && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-gray-600">Sildid:</span>
                  {product.tags.map(tag => (
                    <span
                      key={tag}
                      className="bg-[#4FC1E9]/10 text-[#4FC1E9] px-2 py-1 rounded-full text-sm"
                    >{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Specifications Section */}
        {product.specifications && product.specifications.length > 0 && (
          <div className="border-t">
            <div className="p-4 md:p-8">
              {/* Tab navigation - 13px on mobile using arbitrary value */}
              <div className="flex border-b mb-6 overflow-x-auto">
                <button
                  className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap text-[13px] md:text-base ${
                    activeTab === 0 
                      ? 'text-[#4FC1E9] border-b-2 border-[#4FC1E9]' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab(0)}
                >
                  TOOTEKIRJELDUS
                </button>
                {technicalSpecs && (
                  <button
                    className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap text-[13px] md:text-base ${
                      activeTab === 1 
                        ? 'text-[#4FC1E9] border-b-2 border-[#4FC1E9]' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab(1)}
                  >
                    TEHNILISED ANDMED
                  </button>
                )}
              </div>
              
              {/* Tab content */}
              {activeTab === 0 && productDescription && (
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-4">{productDescription.title}</h2>
                  {productDescription.description && (
                    <p className="text-gray-600 mb-6 leading-relaxed">{productDescription.description}</p>
                  )}
                </div>
              )}
              
              {activeTab === 1 && technicalSpecs && (
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-4">{technicalSpecs.title}</h2>
                  {technicalSpecs.description && (
                    <p className="text-gray-600 mb-6 leading-relaxed">{technicalSpecs.description}</p>
                  )}
                   
                  {technicalSpecs.table?.main && (
                    <div className="mb-6 last:mb-0">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <tbody>
                            {technicalSpecs.table.main.map((row, rowIndex) => (
                              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                <td className="border px-4 py-3 font-medium w-1/3 text-xs md:text-sm">{row.label}</td>
                                <td className="border px-4 py-3 text-xs md:text-sm">{row.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* FAQ Section */}
      {(() => {
        const faqItems = getProductFaqs(product.class, product.category || [], product.brand);
        return (
          <div className="mt-8 md:mt-12 container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Korduma kippuvad küsimused</h2>
              <p className="text-center text-gray-500 mb-8">Leidsid vastused kiirelt — kui ei, siis kirjuta meile!</p>
              <div className="space-y-3">
                {faqItems.map((item, i) => (
                  <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left font-semibold text-gray-800 hover:text-[#4FC1E9] transition-colors duration-200"
                      aria-expanded={openFaq === i}
                    >
                      <span>{item.q}</span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 ml-4 text-[#4FC1E9] transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-96' : 'max-h-0'}`}>
                      <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Customer Reviews Section with gallery */}
      <div className="mt-8 md:mt-12">
        <CustomerReviews 
          productFilter={product.name || selectedVariant.name} 
          customProductId={selectedVariant.id}
          galleryImages={galleryImages}
        />
      </div>

      {/* Related Products Sections */}
      <div className="mt-8 md:mt-12">
        <div className="container mx-auto">
          <ProductDisplaySection title="Populaarsed tooted" products={popularProducts} />
        </div>
      </div>

      <div className="mt-8 md:mt-12">
        <div className="container mx-auto">
          <ProductDisplaySection title="Sooduspakkumised" products={discountedProducts} />
        </div>
      </div>
    </div>
  );
}

export default ProductPage;
